// Catálogo das caixinhas de contexto do upload (objetivo, abordagem, canal,
// presença de CTA, texto do CTA, tom; no vídeo:
// objetivo do vídeo, abordagem, canal e estágio de produção) — vem do arquivo
// `config_context_options` do backend (repo, muda por PR + deploy desde 25/09). Antes
// vivia só em `constants/creativeContext.js`; aquelas listas seguem existindo
// como FALLBACK — por lista: chave que o documento não traz cai na constante.
//
// Por que um `ref` de módulo e não a store `app` (como os tiers): os specs de
// TODO consumidor (CreativeContextFields, Summary, BrandModal, BulkUpload,
// ExperimentView…) montam sem Pinia. Uma store aqui obrigaria cada um deles a
// instalar Pinia só pra ler uma lista — mesmo motivo do cache de módulo do
// `useBrandKit`. Sem `fetchContextOptions()` ter rodado, o catálogo é null e o
// composable devolve as constantes: nada muda de comportamento.
//
// O que é fixo, de propósito, e NÃO vem do documento:
//   - uso do estático (`key_visual`/`anuncio`): enum que roteia o planner;
//   - função da peça (legado): só para rotular linhas antigas;
//   - "Outro…": sentinela do `ContextSelect`, nunca é gravada.
// Desde 2026-09-29 o "Sem CTA" é uma opção da presença de CTA (`cta_presence`)
// e deixou de ser anexado à lista de rótulos do CTA.
import { computed, ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import axios from '@/plugins/axios';
import { useLanguage } from '@/composables/useLanguage';
import {
    CONTEXT_OBJECTIVE_OPTIONS,
    CONTEXT_TONE_OPTIONS,
    CONTEXT_APPROACH_OPTIONS,
    CONTEXT_CHANNEL_OPTIONS,
    CONTEXT_CTA_PRESENCE_OPTIONS,
    CONTEXT_STATIC_USE_OPTIONS,
    CONTEXT_PIECE_FUNCTION_OPTIONS,
    CONTEXT_CTA_OPTIONS,
    CONTEXT_VIDEO_TYPE_OPTIONS,
    CONTEXT_PLATFORM_OPTIONS,
    CONTEXT_PRODUCTION_STAGE_OPTIONS,
    CONTEXT_VIDEO_OBJECTIVE_OPTIONS,
    CONTEXT_CTA_NONE,
    CONTEXT_OPTION_CONFIG_KEYS,
} from '@/constants/creativeContext';

const CONFIG_PATH = '/config/context-options';

// Espelho das chaves do arquivo `config_context_options` do backend.
const CONFIG_FIELDS = Object.values(CONTEXT_OPTION_CONFIG_KEYS);

// Fallback local por lista (descrições = spec do backend §Descrições das opções).
const FALLBACK = {
    objective: CONTEXT_OBJECTIVE_OPTIONS,
    tone: CONTEXT_TONE_OPTIONS,
    approach: CONTEXT_APPROACH_OPTIONS,
    channels: CONTEXT_CHANNEL_OPTIONS,
    ctaPresence: CONTEXT_CTA_PRESENCE_OPTIONS,
    ctaLabel: CONTEXT_CTA_OPTIONS,
    videoObjective: CONTEXT_VIDEO_OBJECTIVE_OPTIONS,
    videoType: CONTEXT_VIDEO_TYPE_OPTIONS,
    platform: CONTEXT_PLATFORM_OPTIONS,
    productionStage: CONTEXT_PRODUCTION_STAGE_OPTIONS,
};

// Documento servido pelo backend, ou null (fallback nas constantes).
const catalog = ref(null);

// Mesma régua estrutural do backend, do lado de cá: o que chega torto é
// ignorado com aviso, nunca adotado — um documento ruim não pode esvaziar os
// selects do upload. Chave AUSENTE não é torta (24/09): um backend que ainda
// não serve as listas novas deixa só elas no fallback. Chave DESCONHECIDA
// (lista que este front ainda não lê) é ignorada, nunca reprova o documento.
export function isValidContextOptionsPayload(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
    return CONFIG_FIELDS.every((field) => {
        if (!(field in payload)) return true;
        const list = payload[field];
        return Array.isArray(list) && list.length > 0 && list.every((o) =>
            o && typeof o.value === 'string' && o.value.trim() !== ''
            && o.label && typeof o.label.pt === 'string' && o.label.pt.trim() !== '');
    });
}

export async function fetchContextOptions() {
    try {
        const response = await axios.get(CONFIG_PATH, { _skipAuthRedirect: true });
        if (isValidContextOptionsPayload(response.data)) {
            catalog.value = response.data;
        } else {
            console.warn('Ignoring invalid context-options config from backend; keeping local defaults.', response.data);
        }
    } catch (error) {
        console.error('Failed to fetch context-options config:', error);
    }
}

// Só para specs: volta ao estado "nunca buscou".
export function resetContextOptionsForTests() {
    catalog.value = null;
}

export function useContextOptions() {
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const tk = (key) => t(`components.common.creativeContext.${key}`);

    // Opção do documento: { value, label: {pt, en?, es?} } → rótulo no idioma da
    // UI, caindo no pt (obrigatório no backend). Opção do fallback: { value,
    // labelKey } → i18n, como sempre foi.
    const resolveLabel = (option) => {
        if (option.labelKey) return tk(option.labelKey);
        // `label.pt` é obrigatório no esquema (backend e `isValidContextOptionsPayload`).
        return option.label[currentLanguage.value] || option.label.pt;
    };

    // Descrição da opção (o ⓘ de cada item do dropdown, 24/09): mesma regra do
    // rótulo — `description: {pt, en?, es?}` do documento, ou `descriptionKey`
    // do fallback. Opcional: sem descrição a opção sai sem ⓘ.
    const resolveDescription = (option) => {
        if (option.descriptionKey) return tk(option.descriptionKey);
        const description = option.description;
        return (description && (description[currentLanguage.value] || description.pt)) || '';
    };

    const listFor = (field) => {
        const fromConfig = catalog.value?.[CONTEXT_OPTION_CONFIG_KEYS[field]];
        return (fromConfig ?? FALLBACK[field]).map((o) => {
            const description = resolveDescription(o);
            return { value: o.value, label: resolveLabel(o), ...(description ? { description } : {}) };
        });
    };

    // Lista fechada da constante (não vem do documento), já resolvida.
    const fixedList = (list) => list.map((o) => {
        const description = resolveDescription(o);
        return { value: o.value, label: tk(o.labelKey), ...(description ? { description } : {}) };
    });

    // Uma lista pronta por lista do formulário, já com rótulo resolvido. O uso e
    // a função da peça (legado) entram pela constante (fechados).
    // `videoObjective` é a lista do campo `objective` quando a peça é vídeo: vem
    // sempre da constante (o `video_objective` do backend é alias da imagem).
    const options = computed(() => ({
        staticUse: fixedList(CONTEXT_STATIC_USE_OPTIONS),
        objective: listFor('objective'),
        videoObjective: listFor('videoObjective'),
        approach: listFor('approach'),
        channels: listFor('channels'),
        ctaPresence: listFor('ctaPresence'),
        ctaLabel: listFor('ctaLabel'),
        tone: listFor('tone'),
        videoType: listFor('videoType'),
        platform: listFor('platform'),
        productionStage: listFor('productionStage'),
        pieceFunction: fixedList(CONTEXT_PIECE_FUNCTION_OPTIONS),
    }));

    // O `objective` é um campo só com duas listas possíveis (imagem e vídeo):
    // quem rotula ou classifica um valor gravado não sabe a mídia, então olha as
    // duas. O `ctaLabel` legado 'Sem CTA' (antes da presença) rotula como a
    // presença "Sem CTA".
    const catalogFor = (field) => {
        if (field === 'objective') return [...options.value.objective, ...options.value.videoObjective];
        if (field === 'ctaLabel') {
            return [...options.value.ctaLabel, ...options.value.ctaPresence.filter((o) => o.value === CONTEXT_CTA_NONE)];
        }
        return options.value[field];
    };

    // Valor canônico gravado → rótulo; fora do catálogo é o escape "Outro…" e o
    // texto digitado é exibido como veio. Lista (canais) → rótulos unidos por
    // ', ', na ordem gravada.
    const labelFor = (field, value) => {
        if (Array.isArray(value)) return value.map((v) => labelFor(field, v)).filter(Boolean).join(', ');
        if (!value) return '';
        const option = catalogFor(field)?.find((o) => o.value === value);
        return option ? option.label : value;
    };

    // Escolha em lista × texto livre (a etapa 2 grava conforme se mexe: lista
    // grava na hora, texto espera parar de digitar). Campo sem catálogo
    // (`content`) é sempre digitado. Mesma semântica do antigo
    // `isTypedContextValue`, agora sobre o catálogo vivo.
    const isTypedValue = (field, value) => {
        if (!value || Array.isArray(value)) return false;
        const list = catalogFor(field);
        return !list || !list.some((o) => o.value === value);
    };

    return { options, labelFor, isTypedValue };
}
